import { createContext, useContext, useState, useEffect } from 'react';

// Supported languages
export const LANGUAGES = {
  de: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  fr: { code: 'fr', name: 'Français', flag: '🇫🇷' }
};

// Translations
const translations = {
  de: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      expenses: 'Ausgaben',
      review: 'Prüfung',
      reports: 'Reports',
      categories: 'Kategorien',
      recurring: 'Wiederkehrend',
      reminders: 'Erinnerungen',
      users: 'Benutzer',
      logout: 'Abmelden'
    },
    // Login
    login: {
      title: 'Anmelden',
      subtitle: 'Geben Sie Ihre Anmeldedaten ein, um fortzufahren',
      email: 'E-Mail',
      password: 'Passwort',
      submit: 'Anmelden',
      loading: 'Anmeldung...',
      welcome: 'Willkommen zurück!',
      error: 'Anmeldung fehlgeschlagen'
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      thisMonth: 'Diesen Monat',
      thisYear: 'Dieses Jahr',
      fixCosts: 'Fixkosten',
      variableCosts: 'Variable Kosten',
      pendingReview: 'Zur Prüfung',
      reminders: 'Erinnerungen',
      byCategory: 'Nach Kategorie',
      monthlyTrend: 'Monatlicher Verlauf',
      recentExpenses: 'Letzte Ausgaben',
      noExpenses: 'Keine Ausgaben',
      openExpenses: 'Offene Zahlungen',
      paidExpenses: 'Bezahlte Ausgaben'
    },
    // Expenses
    expenses: {
      title: 'Ausgaben',
      subtitle: 'Verwalten Sie Ihre Unternehmensausgaben',
      newExpense: 'Neue Ausgabe',
      editExpense: 'Ausgabe bearbeiten',
      search: 'Suchen...',
      filter: 'Filter',
      all: 'Alle',
      fix: 'Fix',
      variable: 'Variabel',
      export: 'CSV Export',
      expenseTitle: 'Titel',
      amount: 'Betrag',
      currency: 'Währung',
      date: 'Datum',
      category: 'Kategorie',
      type: 'Typ',
      vendor: 'Anbieter/Lieferant',
      invoiceNumber: 'Rechnungsnummer',
      description: 'Beschreibung',
      attachment: 'Beleg anhängen',
      aiAnalysis: 'KI-Analyse',
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      confirmDelete: 'Sind Sie sicher?',
      noExpenses: 'Keine Ausgaben gefunden',
      duplicateWarning: 'existiert bereits im System!',
      duplicateInReview: 'Diese Rechnung befindet sich bereits in der Prüfung!',
      paymentStatus: 'Zahlungsstatus',
      paid: 'Bezahlt',
      open: 'Offen',
      allPaymentStatus: 'Alle Status'
    },
    // Review
    review: {
      title: 'Prüfung',
      subtitle: 'Rechnungen prüfen und freigeben',
      toReview: 'Zu prüfen',
      totalOpen: 'Gesamt offen',
      pendingInvoices: 'Zu prüfende Rechnungen',
      noInvoices: 'Keine Rechnungen zur Prüfung',
      allReviewed: 'Alle Rechnungen wurden geprüft',
      reviewNotes: 'Prüfnotizen',
      approve: 'Geprüft',
      book: 'Buchen',
      reject: 'Ablehnen',
      draft: 'Entwurf',
      pendingReview: 'Zur Prüfung',
      reviewed: 'Geprüft',
      booked: 'Gebucht',
      preview: 'Vorschau',
      openPdf: 'PDF öffnen',
      download: 'Herunterladen'
    },
    // Reports
    reports: {
      title: 'Reports',
      subtitle: 'Berichte erstellen, exportieren und versenden',
      periodType: 'Zeitraum-Typ',
      selectPeriod: 'Zeitraum auswählen',
      monthly: 'Monatlich',
      quarterly: 'Quartal',
      semiAnnual: 'Halbjährlich',
      annual: 'Jährlich',
      preview: 'Vorschau',
      exportPrint: 'Export / Druck',
      sendEmail: 'Report per E-Mail versenden',
      sendDescription: 'Report mit allen Ausgaben und Belegen an den Steuerberater senden',
      recipientEmail: 'E-Mail Empfänger',
      recipientName: 'Name des Empfängers',
      taxAdvisor: 'Steuerberater',
      includeAttachments: 'Belege anhängen',
      allInvoicesAsFiles: 'Alle Rechnungen/Belege als Dateien',
      notes: 'Notizen (optional)',
      additionalNotes: 'Zusätzliche Hinweise...',
      sendReport: 'Report senden',
      sending: 'Wird gesendet...',
      sent: 'Erfolgreich gesendet!',
      error: 'Fehler',
      byCategory: 'Nach Kategorie',
      monthlyTrend: 'Monatlicher Verlauf',
      topVendors: 'Top Anbieter',
      byCurrency: 'Nach Währung',
      noData: 'Keine Ausgaben für den ausgewählten Zeitraum gefunden',
      reportContent: 'Report-Inhalt',
      expenseOverview: 'Ausgabenübersicht',
      amountsInEur: 'Beträge in EUR',
      allReceipts: 'Alle Belege',
      csvExport: 'CSV-Export',
      total: 'Gesamtsumme',
      print: 'Drucken',
      expensesCount: 'Ausgaben',
      totalAmount: 'Gesamtsumme',
      fixCosts: 'Fixkosten',
      variableCosts: 'Variable',
      receipts: 'Belege',
      selectPeriodPlaceholder: 'Zeitraum wählen...',
      loading: 'Lade Daten...',
      previewTitle: 'Vorschau',
      nr: 'Nr.',
      date: 'Datum',
      invoiceNr: 'Rechnungsnr.',
      vendor: 'Anbieter',
      category: 'Kategorie',
      amount: 'Betrag',
      receipt: 'Beleg',
      close: 'Schließen',
      exportDruck: 'Exportieren / Drucken',
      noExpensesAvailable: 'Keine Ausgaben vorhanden',
      popupBlocked: 'Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.',
      q1: 'Q1 (Jan-Mär)',
      q2: 'Q2 (Apr-Jun)',
      q3: 'Q3 (Jul-Sep)',
      q4: 'Q4 (Okt-Dez)',
      h1: '1. Halbjahr (Jan-Jun)',
      h2: '2. Halbjahr (Jul-Dez)',
      year: 'Jahr'
    },
    // Categories
    categories: {
      title: 'Kategorien',
      subtitle: 'Ausgabenkategorien verwalten',
      newCategory: 'Neue Kategorie',
      editCategory: 'Kategorie bearbeiten',
      name: 'Name',
      color: 'Farbe',
      icon: 'Icon',
      noCategories: 'Keine Kategorien',
      deleteConfirm: 'Kategorie wirklich löschen?',
      predefined: 'Vordefiniert',
      custom: 'Eigene Kategorien',
      noCustom: 'Keine eigenen Kategorien vorhanden.',
      createHint: 'Erstellen Sie eine neue Kategorie, um loszulegen.'
    },
    // Recurring
    recurring: {
      title: 'Wiederkehrende Ausgaben',
      subtitle: 'Verträge und Abonnements verwalten',
      newRecurring: 'Neue wiederkehrende Ausgabe',
      interval: 'Intervall',
      monthly: 'Monatlich',
      quarterly: 'Quartalsweise',
      yearly: 'Jährlich',
      nextDue: 'Nächste Fälligkeit',
      contractStart: 'Vertragsbeginn',
      contractEnd: 'Vertragsende',
      cancellationNotice: 'Kündigungsfrist (Tage)',
      noRecurring: 'Keine wiederkehrenden Ausgaben',
      activeContracts: 'Aktive Verträge',
      monthlyCosts: 'Monatliche Kosten',
      yearlyCosts: 'Jährliche Kosten',
      newContract: 'Neuer Vertrag',
      editContract: 'Vertrag bearbeiten',
      designation: 'Bezeichnung',
      paymentDay: 'Zahlungstag',
      unlimited: 'Unbefristet',
      reminderHint: 'Sie werden erinnert, bevor die Kündigungsfrist abläuft',
      addContracts: 'Fügen Sie Ihre Verträge und Abos hinzu.',
      deleteConfirm: 'Möchten Sie diesen Vertrag wirklich löschen?'
    },
    // Reminders
    reminders: {
      title: 'Erinnerungen',
      subtitle: 'Zahlungserinnerungen und Kündigungsfristen',
      payment: 'Zahlung',
      cancellation: 'Kündigung',
      dueDate: 'Fällig am',
      acknowledge: 'Erledigt',
      noReminders: 'Keine Erinnerungen',
      openReminders: 'Offene Erinnerungen',
      duePayments: 'Fällige Zahlungen',
      cancellationDeadlines: 'Kündigungsfristen',
      paymentReminder: 'Zahlungserinnerung',
      cancellationDeadline: 'Kündigungsfrist',
      overdue: 'Überfällig',
      urgent: 'Dringend',
      dueSoon: 'Bald fällig',
      days: 'Tage',
      paid: 'Bezahlt',
      done: 'Erledigt',
      doneAt: 'Erledigt am',
      allDone: 'Alles erledigt!',
      noOpenReminders: 'Keine offenen Erinnerungen.',
      noDoneReminders: 'Keine erledigten Erinnerungen.',
      open: 'Offen',
      confirmPayment: 'Zahlung bestätigen',
      expense: 'Ausgabe',
      expectedAmount: 'Erwarteter Betrag',
      paidAmount: 'Bezahlter Betrag',
      deviation: 'Abweichung',
      paymentMethod: 'Zahlungsmethode',
      bankTransfer: 'Überweisung',
      directDebit: 'Lastschrift',
      creditCard: 'Kreditkarte',
      cash: 'Bar',
      confirm: 'Bestätigen',
      sendEmailReminders: 'E-Mail-Erinnerungen senden',
      emailNotConfigured: 'E-Mail nicht konfiguriert',
      emailSent: 'E-Mail-Erinnerungen versendet',
      selectMethod: 'Auswählen...',
      notes: 'Notizen',
      additionalInfo: 'Zusätzliche Informationen...'
    },
    // Users
    users: {
      title: 'Benutzerverwaltung',
      subtitle: 'Benutzer hinzufügen und verwalten',
      newUser: 'Neuer Benutzer',
      name: 'Name',
      email: 'E-Mail',
      password: 'Passwort',
      isAdmin: 'Administrator',
      noUsers: 'Keine Benutzer',
      totalUsers: 'Gesamt Benutzer',
      administrators: 'Administratoren',
      role: 'Rolle',
      admin: 'Admin',
      user: 'Benutzer',
      you: 'Sie',
      create: 'Erstellen',
      creating: 'Erstellen...',
      minChars: 'Mindestens 6 Zeichen',
      canManageUsers: 'Kann Benutzer verwalten',
      deleteConfirm: 'Möchten Sie diesen Benutzer wirklich löschen?',
      manageSystem: 'Verwalten Sie die Benutzer Ihres Systems'
    },
    // Common
    common: {
      save: 'Speichern',
      saved: 'Gespeichert',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      deleted: 'Gelöscht',
      edit: 'Bearbeiten',
      close: 'Schließen',
      loading: 'Laden...',
      error: 'Fehler aufgetreten',
      success: 'Erfolgreich',
      yes: 'Ja',
      no: 'Nein',
      search: 'Suchen',
      noResults: 'Keine Ergebnisse',
      actions: 'Aktionen',
      status: 'Status',
      date: 'Datum',
      amount: 'Betrag',
      total: 'Gesamt',
      receipts: 'Belege',
      expenses: 'Ausgaben',
      confirmDelete: 'Möchten Sie wirklich löschen?'
    }
  },
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      expenses: 'Expenses',
      review: 'Review',
      reports: 'Reports',
      categories: 'Categories',
      recurring: 'Recurring',
      reminders: 'Reminders',
      users: 'Users',
      logout: 'Logout'
    },
    // Login
    login: {
      title: 'Login',
      subtitle: 'Enter your credentials to continue',
      email: 'Email',
      password: 'Password',
      submit: 'Login',
      loading: 'Logging in...',
      welcome: 'Welcome back!',
      error: 'Login failed'
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      fixCosts: 'Fixed Costs',
      variableCosts: 'Variable Costs',
      pendingReview: 'Pending Review',
      reminders: 'Reminders',
      byCategory: 'By Category',
      monthlyTrend: 'Monthly Trend',
      recentExpenses: 'Recent Expenses',
      noExpenses: 'No expenses',
      openExpenses: 'Open Payments',
      paidExpenses: 'Paid Expenses'
    },
    // Expenses
    expenses: {
      title: 'Expenses',
      subtitle: 'Manage your business expenses',
      newExpense: 'New Expense',
      editExpense: 'Edit Expense',
      search: 'Search...',
      filter: 'Filter',
      all: 'All',
      fix: 'Fixed',
      variable: 'Variable',
      export: 'CSV Export',
      expenseTitle: 'Title',
      amount: 'Amount',
      currency: 'Currency',
      date: 'Date',
      category: 'Category',
      type: 'Type',
      vendor: 'Vendor/Supplier',
      invoiceNumber: 'Invoice Number',
      description: 'Description',
      attachment: 'Attach Receipt',
      aiAnalysis: 'AI Analysis',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      confirmDelete: 'Are you sure?',
      noExpenses: 'No expenses found',
      duplicateWarning: 'already exists in the system!',
      duplicateInReview: 'This invoice is already in review!',
      paymentStatus: 'Payment Status',
      paid: 'Paid',
      open: 'Open',
      allPaymentStatus: 'All Statuses'
    },
    // Review
    review: {
      title: 'Review',
      subtitle: 'Review and approve invoices',
      toReview: 'To Review',
      totalOpen: 'Total Open',
      pendingInvoices: 'Pending Invoices',
      noInvoices: 'No invoices to review',
      allReviewed: 'All invoices have been reviewed',
      reviewNotes: 'Review Notes',
      approve: 'Approve',
      book: 'Book',
      reject: 'Reject',
      draft: 'Draft',
      pendingReview: 'Pending Review',
      reviewed: 'Reviewed',
      booked: 'Booked',
      preview: 'Preview',
      openPdf: 'Open PDF',
      download: 'Download'
    },
    // Reports
    reports: {
      title: 'Reports',
      subtitle: 'Create, export and send reports',
      periodType: 'Period Type',
      selectPeriod: 'Select Period',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiAnnual: 'Semi-Annual',
      annual: 'Annual',
      preview: 'Preview',
      exportPrint: 'Export / Print',
      sendEmail: 'Send Report via Email',
      sendDescription: 'Send report with all expenses and receipts to your tax advisor',
      recipientEmail: 'Recipient Email',
      recipientName: 'Recipient Name',
      taxAdvisor: 'Tax Advisor',
      includeAttachments: 'Include Attachments',
      allInvoicesAsFiles: 'All invoices/receipts as files',
      notes: 'Notes (optional)',
      additionalNotes: 'Additional notes...',
      sendReport: 'Send Report',
      sending: 'Sending...',
      sent: 'Successfully sent!',
      error: 'Error',
      byCategory: 'By Category',
      monthlyTrend: 'Monthly Trend',
      topVendors: 'Top Vendors',
      byCurrency: 'By Currency',
      noData: 'No expenses found for the selected period',
      reportContent: 'Report Content',
      expenseOverview: 'Expense Overview',
      amountsInEur: 'Amounts in EUR',
      allReceipts: 'All Receipts',
      csvExport: 'CSV Export',
      total: 'Total',
      print: 'Print',
      expensesCount: 'Expenses',
      totalAmount: 'Total Amount',
      fixCosts: 'Fixed Costs',
      variableCosts: 'Variable',
      receipts: 'Receipts',
      selectPeriodPlaceholder: 'Select period...',
      loading: 'Loading data...',
      previewTitle: 'Preview',
      nr: 'No.',
      date: 'Date',
      invoiceNr: 'Invoice No.',
      vendor: 'Vendor',
      category: 'Category',
      amount: 'Amount',
      receipt: 'Receipt',
      close: 'Close',
      exportDruck: 'Export / Print',
      noExpensesAvailable: 'No expenses available',
      popupBlocked: 'Popup was blocked. Please allow popups for this page.',
      q1: 'Q1 (Jan-Mar)',
      q2: 'Q2 (Apr-Jun)',
      q3: 'Q3 (Jul-Sep)',
      q4: 'Q4 (Oct-Dec)',
      h1: '1st Half (Jan-Jun)',
      h2: '2nd Half (Jul-Dec)',
      year: 'Year'
    },
    // Categories
    categories: {
      title: 'Categories',
      subtitle: 'Manage expense categories',
      newCategory: 'New Category',
      editCategory: 'Edit Category',
      name: 'Name',
      color: 'Color',
      icon: 'Icon',
      noCategories: 'No categories',
      deleteConfirm: 'Really delete category?',
      predefined: 'Predefined',
      custom: 'Custom Categories',
      noCustom: 'No custom categories.',
      createHint: 'Create a new category to get started.'
    },
    // Recurring
    recurring: {
      title: 'Recurring Expenses',
      subtitle: 'Manage contracts and subscriptions',
      newRecurring: 'New Recurring Expense',
      newContract: 'New Contract',
      editContract: 'Edit Contract',
      interval: 'Interval',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      nextDue: 'Next Due',
      contractStart: 'Contract Start',
      contractEnd: 'Contract End',
      cancellationNotice: 'Cancellation Notice (Days)',
      noRecurring: 'No recurring expenses',
      activeContracts: 'Active Contracts',
      monthlyCosts: 'Monthly Costs',
      yearlyCosts: 'Yearly Costs',
      designation: 'Designation',
      paymentDay: 'Payment Day',
      unlimited: 'Unlimited',
      reminderHint: 'You will be reminded before the cancellation deadline expires',
      addContracts: 'Add your contracts and subscriptions.',
      deleteConfirm: 'Do you really want to delete this contract?'
    },
    // Reminders
    reminders: {
      title: 'Reminders',
      subtitle: 'Upcoming payments and cancellation deadlines',
      payment: 'Payment',
      cancellation: 'Cancellation',
      dueDate: 'Due Date',
      acknowledge: 'Done',
      noReminders: 'No reminders',
      openReminders: 'Open Reminders',
      duePayments: 'Due Payments',
      cancellationDeadlines: 'Cancellation Deadlines',
      paymentReminder: 'Payment Reminder',
      cancellationDeadline: 'Cancellation Deadline',
      overdue: 'Overdue',
      urgent: 'Urgent',
      dueSoon: 'Due Soon',
      days: 'Days',
      paid: 'Paid',
      done: 'Done',
      doneAt: 'Done on',
      allDone: 'All done!',
      noOpenReminders: 'No open reminders.',
      noDoneReminders: 'No completed reminders.',
      open: 'Open',
      confirmPayment: 'Confirm Payment',
      expense: 'Expense',
      expectedAmount: 'Expected Amount',
      paidAmount: 'Paid Amount',
      deviation: 'Deviation',
      paymentMethod: 'Payment Method',
      bankTransfer: 'Bank Transfer',
      directDebit: 'Direct Debit',
      creditCard: 'Credit Card',
      cash: 'Cash',
      confirm: 'Confirm',
      sendEmailReminders: 'Send Email Reminders',
      emailNotConfigured: 'Email not configured',
      emailSent: 'Email reminders sent',
      selectMethod: 'Select...',
      notes: 'Notes',
      additionalInfo: 'Additional information...'
    },
    // Users
    users: {
      title: 'User Management',
      subtitle: 'Add and manage users',
      newUser: 'New User',
      name: 'Name',
      email: 'Email',
      password: 'Password',
      isAdmin: 'Administrator',
      noUsers: 'No users',
      totalUsers: 'Total Users',
      administrators: 'Administrators',
      role: 'Role',
      admin: 'Admin',
      user: 'User',
      you: 'You',
      create: 'Create',
      creating: 'Creating...',
      minChars: 'Minimum 6 characters',
      canManageUsers: 'Can manage users',
      deleteConfirm: 'Do you really want to delete this user?',
      manageSystem: 'Manage your system users'
    },
    // Common
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      yes: 'Yes',
      no: 'No',
      search: 'Search',
      noResults: 'No results',
      actions: 'Actions',
      status: 'Status',
      date: 'Date',
      amount: 'Amount',
      total: 'Total',
      receipts: 'Receipts',
      expenses: 'Expenses',
      saved: 'Saved',
      deleted: 'Deleted',
      confirmDelete: 'Are you sure you want to delete?'
    }
  },
  fr: {
    // Navigation
    nav: {
      dashboard: 'Tableau de bord',
      expenses: 'Dépenses',
      review: 'Révision',
      reports: 'Rapports',
      categories: 'Catégories',
      recurring: 'Récurrent',
      reminders: 'Rappels',
      users: 'Utilisateurs',
      logout: 'Déconnexion'
    },
    // Login
    login: {
      title: 'Connexion',
      subtitle: 'Entrez vos identifiants pour continuer',
      email: 'E-mail',
      password: 'Mot de passe',
      submit: 'Se connecter',
      loading: 'Connexion...',
      welcome: 'Bienvenue!',
      error: 'Échec de connexion'
    },
    // Dashboard
    dashboard: {
      title: 'Tableau de bord',
      thisMonth: 'Ce mois',
      thisYear: 'Cette année',
      fixCosts: 'Coûts fixes',
      variableCosts: 'Coûts variables',
      pendingReview: 'En attente',
      reminders: 'Rappels',
      byCategory: 'Par catégorie',
      monthlyTrend: 'Tendance mensuelle',
      recentExpenses: 'Dépenses récentes',
      noExpenses: 'Aucune dépense',
      openExpenses: 'Paiements en attente',
      paidExpenses: 'Dépenses payées'
    },
    // Expenses
    expenses: {
      title: 'Dépenses',
      subtitle: 'Gérez vos dépenses professionnelles',
      newExpense: 'Nouvelle dépense',
      editExpense: 'Modifier la dépense',
      search: 'Rechercher...',
      filter: 'Filtrer',
      all: 'Tout',
      fix: 'Fixe',
      variable: 'Variable',
      export: 'Export CSV',
      expenseTitle: 'Titre',
      amount: 'Montant',
      currency: 'Devise',
      date: 'Date',
      category: 'Catégorie',
      type: 'Type',
      vendor: 'Fournisseur',
      invoiceNumber: 'Numéro de facture',
      description: 'Description',
      attachment: 'Joindre un reçu',
      aiAnalysis: 'Analyse IA',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      confirmDelete: 'Êtes-vous sûr?',
      noExpenses: 'Aucune dépense trouvée',
      duplicateWarning: 'existe déjà dans le système!',
      duplicateInReview: 'Cette facture est déjà en révision!',
      paymentStatus: 'Statut de paiement',
      paid: 'Payé',
      open: 'En attente',
      allPaymentStatus: 'Tous les statuts'
    },
    // Review
    review: {
      title: 'Révision',
      subtitle: 'Réviser et approuver les factures',
      toReview: 'À réviser',
      totalOpen: 'Total ouvert',
      pendingInvoices: 'Factures en attente',
      noInvoices: 'Aucune facture à réviser',
      allReviewed: 'Toutes les factures ont été révisées',
      reviewNotes: 'Notes de révision',
      approve: 'Approuver',
      book: 'Comptabiliser',
      reject: 'Rejeter',
      draft: 'Brouillon',
      pendingReview: 'En révision',
      reviewed: 'Révisé',
      booked: 'Comptabilisé',
      preview: 'Aperçu',
      openPdf: 'Ouvrir PDF',
      download: 'Télécharger'
    },
    // Reports
    reports: {
      title: 'Rapports',
      subtitle: 'Créer, exporter et envoyer des rapports',
      periodType: 'Type de période',
      selectPeriod: 'Sélectionner la période',
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      semiAnnual: 'Semestriel',
      annual: 'Annuel',
      preview: 'Aperçu',
      exportPrint: 'Exporter / Imprimer',
      sendEmail: 'Envoyer le rapport par e-mail',
      sendDescription: 'Envoyer le rapport avec toutes les dépenses et reçus au conseiller fiscal',
      recipientEmail: 'E-mail du destinataire',
      recipientName: 'Nom du destinataire',
      taxAdvisor: 'Conseiller fiscal',
      includeAttachments: 'Inclure les pièces jointes',
      allInvoicesAsFiles: 'Toutes les factures/reçus en fichiers',
      notes: 'Notes (optionnel)',
      additionalNotes: 'Notes supplémentaires...',
      sendReport: 'Envoyer le rapport',
      sending: 'Envoi en cours...',
      sent: 'Envoyé avec succès!',
      error: 'Erreur',
      byCategory: 'Par catégorie',
      monthlyTrend: 'Tendance mensuelle',
      topVendors: 'Top fournisseurs',
      byCurrency: 'Par devise',
      noData: 'Aucune dépense pour la période sélectionnée',
      reportContent: 'Contenu du rapport',
      expenseOverview: 'Aperçu des dépenses',
      amountsInEur: 'Montants en EUR',
      allReceipts: 'Tous les reçus',
      csvExport: 'Export CSV',
      total: 'Total',
      print: 'Imprimer',
      expensesCount: 'Dépenses',
      totalAmount: 'Montant total',
      fixCosts: 'Coûts fixes',
      variableCosts: 'Variable',
      receipts: 'Reçus',
      selectPeriodPlaceholder: 'Sélectionner la période...',
      loading: 'Chargement...',
      previewTitle: 'Aperçu',
      nr: 'N°',
      date: 'Date',
      invoiceNr: 'N° facture',
      vendor: 'Fournisseur',
      category: 'Catégorie',
      amount: 'Montant',
      receipt: 'Reçu',
      close: 'Fermer',
      exportDruck: 'Exporter / Imprimer',
      noExpensesAvailable: 'Aucune dépense disponible',
      popupBlocked: 'Le popup a été bloqué. Veuillez autoriser les popups pour cette page.',
      q1: 'T1 (Jan-Mar)',
      q2: 'T2 (Avr-Jun)',
      q3: 'T3 (Jul-Sep)',
      q4: 'T4 (Oct-Déc)',
      h1: '1er semestre (Jan-Jun)',
      h2: '2e semestre (Jul-Déc)',
      year: 'Année'
    },
    // Categories
    categories: {
      title: 'Catégories',
      subtitle: 'Gérer les catégories de dépenses',
      newCategory: 'Nouvelle catégorie',
      editCategory: 'Modifier la catégorie',
      name: 'Nom',
      color: 'Couleur',
      icon: 'Icône',
      noCategories: 'Aucune catégorie',
      deleteConfirm: 'Vraiment supprimer la catégorie?',
      predefined: 'Prédéfini',
      custom: 'Catégories personnalisées',
      noCustom: 'Aucune catégorie personnalisée.',
      createHint: 'Créez une nouvelle catégorie pour commencer.'
    },
    // Recurring
    recurring: {
      title: 'Dépenses récurrentes',
      subtitle: 'Gérer les contrats et abonnements',
      newRecurring: 'Nouvelle dépense récurrente',
      newContract: 'Nouveau contrat',
      editContract: 'Modifier le contrat',
      interval: 'Intervalle',
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      yearly: 'Annuel',
      nextDue: 'Prochaine échéance',
      contractStart: 'Début du contrat',
      contractEnd: 'Fin du contrat',
      cancellationNotice: 'Préavis de résiliation (jours)',
      noRecurring: 'Aucune dépense récurrente',
      activeContracts: 'Contrats actifs',
      monthlyCosts: 'Coûts mensuels',
      yearlyCosts: 'Coûts annuels',
      designation: 'Désignation',
      paymentDay: 'Jour de paiement',
      unlimited: 'Illimité',
      reminderHint: 'Vous serez rappelé avant l\'expiration du délai de résiliation',
      addContracts: 'Ajoutez vos contrats et abonnements.',
      deleteConfirm: 'Voulez-vous vraiment supprimer ce contrat?'
    },
    // Reminders
    reminders: {
      title: 'Rappels',
      subtitle: 'Paiements à venir et délais de résiliation',
      payment: 'Paiement',
      cancellation: 'Résiliation',
      dueDate: 'Date d\'échéance',
      acknowledge: 'Terminé',
      noReminders: 'Aucun rappel',
      openReminders: 'Rappels ouverts',
      duePayments: 'Paiements dus',
      cancellationDeadlines: 'Délais de résiliation',
      paymentReminder: 'Rappel de paiement',
      cancellationDeadline: 'Délai de résiliation',
      overdue: 'En retard',
      urgent: 'Urgent',
      dueSoon: 'Bientôt dû',
      days: 'Jours',
      paid: 'Payé',
      done: 'Terminé',
      doneAt: 'Terminé le',
      allDone: 'Tout est fait!',
      noOpenReminders: 'Aucun rappel ouvert.',
      noDoneReminders: 'Aucun rappel terminé.',
      open: 'Ouvert',
      confirmPayment: 'Confirmer le paiement',
      expense: 'Dépense',
      expectedAmount: 'Montant attendu',
      paidAmount: 'Montant payé',
      deviation: 'Écart',
      paymentMethod: 'Mode de paiement',
      bankTransfer: 'Virement bancaire',
      directDebit: 'Prélèvement',
      creditCard: 'Carte de crédit',
      cash: 'Espèces',
      confirm: 'Confirmer',
      sendEmailReminders: 'Envoyer les rappels par e-mail',
      emailNotConfigured: 'E-mail non configuré',
      emailSent: 'Rappels par e-mail envoyés',
      selectMethod: 'Sélectionner...',
      notes: 'Notes',
      additionalInfo: 'Informations supplémentaires...'
    },
    // Users
    users: {
      title: 'Gestion des utilisateurs',
      subtitle: 'Ajouter et gérer les utilisateurs',
      newUser: 'Nouvel utilisateur',
      name: 'Nom',
      email: 'E-mail',
      password: 'Mot de passe',
      isAdmin: 'Administrateur',
      noUsers: 'Aucun utilisateur',
      totalUsers: 'Total utilisateurs',
      administrators: 'Administrateurs',
      role: 'Rôle',
      admin: 'Admin',
      user: 'Utilisateur',
      you: 'Vous',
      create: 'Créer',
      creating: 'Création...',
      minChars: 'Minimum 6 caractères',
      canManageUsers: 'Peut gérer les utilisateurs',
      deleteConfirm: 'Voulez-vous vraiment supprimer cet utilisateur?',
      manageSystem: 'Gérez les utilisateurs de votre système'
    },
    // Common
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      close: 'Fermer',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      yes: 'Oui',
      no: 'Non',
      search: 'Rechercher',
      noResults: 'Aucun résultat',
      actions: 'Actions',
      status: 'Statut',
      date: 'Date',
      amount: 'Montant',
      total: 'Total',
      receipts: 'Reçus',
      expenses: 'Dépenses',
      saved: 'Enregistré',
      deleted: 'Supprimé',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer?'
    }
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // Get saved language or detect from browser
    const saved = localStorage.getItem('language');
    if (saved && translations[saved]) {
      return saved;
    }
    // Detect browser language
    const browserLang = navigator.language.split('-')[0];
    return translations[browserLang] ? browserLang : 'de';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      t,
      languages: LANGUAGES 
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
